/**
 * FORMEONLY — free Leather Ida Mini with $75+ (text insiders), code-gated
 * Theme: Impulse 7.4.0 (Archetype Themes) — RRIVETER
 *
 * WHAT THIS FILE DOES
 *   Once the customer has applied the code FORMEONLY and the eligible subtotal reaches
 *   $75, keeps exactly ONE gift (Leather Ida Mini) in the cart. Below $75, without the
 *   code, or once the gift is SOLD OUT, keeps zero. It only moves quantities around via
 *   the Cart AJAX API.
 *
 *   Limited stock (74 units): the gift product must NOT continue selling when out of
 *   stock. Before adding it, and whenever a gift line is in the cart, the script asks
 *   /products/<handle>.js whether the variant is still available. Sold out = the gift is
 *   not added (or is taken back out, so checkout is never blocked by an unsellable line)
 *   and the code field says the minis are all claimed.
 *
 * WHAT THIS FILE DOES NOT DO
 *   It never touches prices. Making the gift free is the job of the native Buy X Get Y
 *   discount code FORMEONLY. If the discount is off, the gift is simply added at full
 *   price — and then rolled back out by verifyDiscountOrRollback — never faked at $0.
 *
 * LOOP SAFETY (read before editing)
 *   1. Reconciliation is idempotent: it computes a target state and writes it once.
 *      A second pass over the same cart produces action "none", so event echoes die out.
 *   2. Our own cart writes bypass the patched window.fetch (we hold the original
 *      reference), so we never re-trigger our own interceptor.
 *   3. A mutex (state.busy) serialises passes; concurrent triggers collapse into one
 *      queued re-run instead of racing.
 *   4. A cart signature check short-circuits repeat passes over an unchanged cart.
 *   5. A burst limiter pauses reconciliation if something upstream starts flapping.
 *   6. Quantities are written absolutely (update.js sets, never increments), so a
 *      double-fire can never stack gifts.
 *
 * Forked from bts-mystery-gift.js (same loop-safety design and tests).
 *
 * Config comes from #FormeonlyGiftConfig (rendered by snippets/formeonly-gift.liquid)
 * or from a window.FormeonlyGiftConfig object set before this script loads.
 */
(function () {
  'use strict';

  var VERSION = '1.0.0';

  /* ------------------------------------------------------------------ *
   * Tunables
   * ------------------------------------------------------------------ */
  var CONFIG_ELEMENT_ID = 'FormeonlyGiftConfig';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var DEBOUNCE_MS = 200;
  var BURST_LIMIT = 8; // reconcile passes...
  var BURST_WINDOW_MS = 5000; // ...allowed inside this window
  var BURST_COOLDOWN_MS = 5000;
  var MAX_WRITE_FAILURES = 3;
  var HARD_MAX_GIFTS = 1; // FORMEONLY is one gift per order, whatever the config says
  var STOCK_TTL_MS = 15000; // how long a live stock answer is trusted

  /* Captured before anything else can patch them. */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var DEFAULTS = {
    enabled: false,
    productId: null,
    handle: null,
    variantId: null,
    available: true,
    thresholdCents: 7500,
    maxGifts: 1,
    basis: 'original', // 'original' = before other discounts, 'final' = after
    reloadCartPage: true,
    debug: false,

    // Lines that never count toward the $75 — mirrors the discount's qualifying
    // collection ("DiscountEligible (excluding heirloom, gift cards)"). Gift cards are
    // always excluded. Matching is case-insensitive.
    excludeProductTypes: [],
    excludeHandleKeywords: ['heirloom'],

    // Discount-code gate
    codeRequired: true,
    code: 'FORMEONLY',
    codeAttribute: 'FORMEONLY Gift Code', // cart attribute used as the durable applied-flag
    autoMountCodeForm: true,
    currencySymbol: '$',

    // Keep the gift out of the storefront (backstops for the product template)
    blockDirectAdd: true,
    hideFromListings: true,
    labels: {
      prompt: 'Text insider? Enter your code',
      placeholder: 'Enter code',
      apply: 'Apply',
      remove: 'Remove',
      // %amount% and %count% are the placeholder tokens. They are deliberately NOT
      // {{ }} — these strings pass through Liquid `default:` filters in the snippet,
      // and a literal {{ }} there would close the Liquid tag early.
      applied: 'applied — your free Leather Ida Mini is in your cart.',
      appliedPending: 'applied — add %amount% more to get your free Leather Ida Mini.',
      soldOut: '— sorry, every free Leather Ida Mini has been claimed.',
      invalid: 'That code is not valid, or this offer has ended.',
      failed: 'Could not apply that code. Please try again.'
    }
  };

  /* ------------------------------------------------------------------ *
   * Config
   * ------------------------------------------------------------------ */
  function readConfig() {
    var raw = null;
    var el = document.getElementById(CONFIG_ELEMENT_ID);

    if (el) {
      try {
        raw = JSON.parse(el.textContent || el.innerHTML || '{}');
      } catch (e) {
        raw = null;
      }
    }
    if (!raw && window.FormeonlyGiftConfig) raw = window.FormeonlyGiftConfig;

    var cfg = {};
    for (var key in DEFAULTS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS, key)) {
        cfg[key] = raw && raw[key] !== undefined && raw[key] !== null ? raw[key] : DEFAULTS[key];
      }
    }

    cfg.productId = cfg.productId ? Number(cfg.productId) : null;
    cfg.variantId = cfg.variantId ? Number(cfg.variantId) : null;
    cfg.thresholdCents = Math.round(Number(cfg.thresholdCents) || 0);
    cfg.maxGifts = Math.max(0, Math.round(Number(cfg.maxGifts) || 0));
    cfg.excludeProductTypes = toLowerList(cfg.excludeProductTypes);
    cfg.excludeHandleKeywords = toLowerList(cfg.excludeHandleKeywords);
    cfg.code = String(cfg.code || '').trim().toUpperCase();
    cfg.codeAttribute = String(cfg.codeAttribute || DEFAULTS.codeAttribute);

    var labels = {};
    for (var labelKey in DEFAULTS.labels) {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS.labels, labelKey)) {
        labels[labelKey] =
          cfg.labels && cfg.labels[labelKey] ? cfg.labels[labelKey] : DEFAULTS.labels[labelKey];
      }
    }
    cfg.labels = labels;

    if (cfg.codeRequired && !cfg.code) cfg.codeRequired = false;

    return cfg;
  }

  /** "Heirloom, Gift Card" or ['Heirloom'] -> ['heirloom', 'gift card'] */
  function toLowerList(value) {
    var list = Array.isArray(value) ? value : String(value || '').split(',');
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = String(list[i] || '').trim().toLowerCase();
      if (item) out.push(item);
    }
    return out;
  }

  var cfg = readConfig();

  var state = {
    busy: false,
    queued: false,
    disabled: false,
    writeFailures: 0,
    lastSettledSignature: null,
    passTimes: [],
    pausedUntil: 0,
    debounceTimer: null,
    codeApplied: false,
    codeBusy: false,
    codeRejected: false,
    reArmed: false,
    lastCart: null,
    mountWarned: false,
    // Live stock of the gift variant. Starts from the Liquid snapshot, refreshed from
    // /products/<handle>.js before any add and while a gift line sits in the cart.
    giftAvailable: cfg.available !== false,
    stockCheckedAt: 0
  };

  function log() {
    if (!cfg.debug || !window.console) return;
    var args = ['[formeonly-gift]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function warn() {
    if (!window.console) return;
    var args = ['[formeonly-gift]'].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }

  /* ------------------------------------------------------------------ *
   * Pure logic — unit tested in /tests/reconcile.test.js
   * ------------------------------------------------------------------ */

  /** Is this cart line the gift product (Leather Ida Mini)? Matches every variant of it. */
  function isGiftLine(line) {
    if (!line) return false;
    if (cfg.productId && Number(line.product_id) === cfg.productId) return true;
    if (cfg.handle && line.handle === cfg.handle) return true;
    if (cfg.variantId && Number(line.variant_id) === cfg.variantId) return true;
    return false;
  }

  /** Line price used for the threshold, in cents. */
  function lineBasisPrice(line) {
    if (cfg.basis === 'final') {
      return Number(line.final_line_price !== undefined ? line.final_line_price : line.line_price) || 0;
    }
    return Number(line.original_line_price !== undefined ? line.original_line_price : line.line_price) || 0;
  }

  /**
   * Does this line count toward the $75? Mirrors the discount's qualifying collection:
   * never the gift itself, never gift cards, never the configured exclusions (heirloom).
   * If this is looser than the real collection, the rollback in verifyDiscountOrRollback
   * still catches it — Shopify reports the code as not applicable and the gift comes out.
   */
  function isEligibleLine(line) {
    if (!line || isGiftLine(line)) return false;
    if (line.gift_card) return false;

    var type = String(line.product_type || '').trim().toLowerCase();
    for (var i = 0; i < cfg.excludeProductTypes.length; i++) {
      if (type === cfg.excludeProductTypes[i]) return false;
    }

    var handle = String(line.handle || '').toLowerCase();
    for (var j = 0; j < cfg.excludeHandleKeywords.length; j++) {
      if (handle.indexOf(cfg.excludeHandleKeywords[j]) > -1) return false;
    }
    return true;
  }

  /**
   * Merchandise subtotal that counts toward the promotion, in cents.
   * The gift is excluded entirely, so it can never fund its own threshold.
   */
  function eligibleSubtotal(cart) {
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (!isEligibleLine(items[i])) continue;
      total += lineBasisPrice(items[i]);
    }
    return total;
  }

  /**
   * Has the promotion code been applied to this cart?
   *
   * Shopify does not expose manual discount codes through cart.discount_applications,
   * so there are two signals, checked in order of trustworthiness:
   *
   *   1. cart.discount_codes — present on newer Cart AJAX API responses. If the code is
   *      listed with applicable === false (expired, inactive, not eligible) it counts as
   *      NOT applied, so the cart never holds a gift the customer would be charged for.
   *   2. A cart attribute we set ourselves when the code was applied. This is the
   *      durable fallback: it survives page loads and is readable from /cart.js.
   */
  function isCodeApplied(cart) {
    if (!cfg.codeRequired) return true;
    if (!cart) return false;
    if (state.codeRejected) return false; // proven undeliverable this session

    var codes = cart.discount_codes;
    if (codes && codes.length) {
      for (var i = 0; i < codes.length; i++) {
        var entry = codes[i] || {};
        var value = String(entry.code || entry).trim().toUpperCase();
        // Present is enough to open the gate — see codeApplicability() for why we do NOT
        // require applicable:true here.
        if (value === cfg.code) return true;
      }
    }

    return codeStoredOnCart(cart);
  }

  /**
   * What does Shopify say about our code?  true / false / null (not reported).
   *
   * This is deliberately NOT the gate. A Buy X Get Y code is reported as not applicable
   * while the cart is below the minimum purchase amount — and, critically, can also be
   * reported that way when the "customer gets" product is not in the cart yet. Gating on
   * applicable:true would deadlock: we would wait for Shopify to approve the code, while
   * Shopify waits for the gift it is supposed to discount.
   *
   * So the gate is "code present", and this is used afterwards to verify: once the gift
   * IS in a qualifying cart and Shopify still says no, the code cannot deliver and the
   * gift is pulled back out (see verifyDiscountOrRollback).
   */
  function codeApplicability(cart) {
    var codes = cart && cart.discount_codes;
    if (!codes || !codes.length) return null;
    for (var i = 0; i < codes.length; i++) {
      var entry = codes[i] || {};
      var value = String(entry.code || entry).trim().toUpperCase();
      if (value === cfg.code) return entry.applicable !== false;
    }
    return null;
  }

  /**
   * Gifts the cart has EARNED: 1 once the code is applied and eligible spend >= $75.
   * Stock is deliberately not part of this — see planReconciliation — so the message can
   * tell "you qualify but they're gone" apart from "spend more".
   */
  function computeRequired(cart) {
    if (!cfg.enabled || !cfg.thresholdCents) return 0;
    if (!isCodeApplied(cart)) return 0;
    var required = Math.floor(eligibleSubtotal(cart) / cfg.thresholdCents);
    if (cfg.maxGifts > 0) required = Math.min(required, cfg.maxGifts);
    return Math.max(0, Math.min(required, HARD_MAX_GIFTS));
  }

  /**
   * Work out the single write that moves the cart to the target state.
   * Returns { action, required, current, updates?, addQuantity?, reason? }
   */
  function planReconciliation(cart) {
    var plan = { action: 'none', required: 0, current: 0 };

    if (!cfg.enabled) return withReason(plan, 'disabled');
    if (!cfg.variantId) return withReason(plan, 'no-variant-configured');

    var items = (cart && cart.items) || [];
    var giftLines = [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftLine(items[i])) giftLines.push(items[i]);
    }

    var current = 0;
    for (var j = 0; j < giftLines.length; j++) current += Number(giftLines[j].quantity) || 0;

    plan.earned = computeRequired(cart);
    plan.soldOut = !state.giftAvailable;
    // Sold out: the cart must hold zero gifts. A gift line that is already there cannot
    // be bought (inventory is not reserved by carts), and would block checkout.
    plan.required = plan.soldOut ? 0 : plan.earned;
    plan.current = current;
    plan.codeApplied = isCodeApplied(cart);

    if (plan.soldOut && plan.earned > 0 && giftLines.length === 0) {
      return withReason(plan, 'gift-sold-out');
    }

    // Nothing in the cart yet and nothing owed.
    if (giftLines.length === 0) {
      if (plan.required > 0) {
        plan.action = 'add';
        plan.addQuantity = plan.required;
        return withReason(plan, 'add-missing-gift');
      }
      return withReason(plan, plan.codeApplied ? 'nothing-owed' : 'code-not-applied');
    }

    // Exactly one gift line already holding the right quantity.
    if (giftLines.length === 1 && current === plan.required) {
      return withReason(plan, 'in-sync');
    }

    // One atomic update: first line carries the whole quantity, duplicates go to zero.
    var updates = {};
    for (var k = 0; k < giftLines.length; k++) {
      updates[giftLines[k].key] = k === 0 ? plan.required : 0;
    }
    plan.action = 'update';
    plan.updates = updates;
    return withReason(
      plan,
      plan.required === 0
        ? plan.soldOut && plan.earned > 0
          ? 'remove-gift-sold-out'
          : plan.codeApplied
          ? 'remove-gift'
          : 'remove-gift-code-missing'
        : giftLines.length > 1
        ? 'consolidate-duplicates'
        : 'resync-quantity'
    );
  }

  function withReason(plan, reason) {
    plan.reason = reason;
    return plan;
  }

  /**
   * Cheap identity for a cart state, used to skip redundant passes.
   * The code gate is part of the identity — applying or removing the code must be able
   * to wake reconciliation up even when no line item changed.
   */
  function cartSignature(cart) {
    var items = (cart && cart.items) || [];
    var parts = [isCodeApplied(cart) ? 'code:1' : 'code:0', state.giftAvailable ? 'stock:1' : 'stock:0'];
    for (var i = 0; i < items.length; i++) {
      parts.push(items[i].key + ':' + items[i].quantity + ':' + lineBasisPrice(items[i]));
    }
    return parts.join('|');
  }

  /* ------------------------------------------------------------------ *
   * Cart I/O — always through nativeFetch so we never trip our own hooks
   * ------------------------------------------------------------------ */
  function getJSON(url) {
    return nativeFetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(readJSON);
  }

  function postJSON(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(readJSON);
  }

  function readJSON(response) {
    if (!response || !response.ok) {
      var status = response ? response.status : 0;
      var error = new Error('Cart request failed (' + status + ')');
      error.status = status;
      return Promise.reject(error);
    }
    return response.json();
  }

  function fetchCart() {
    return getJSON('/cart.js');
  }

  /* ------------------------------------------------------------------ *
   * Live gift stock
   * ------------------------------------------------------------------ */

  /**
   * Is the gift variant still purchasable right now?
   *
   * Only asked when it matters — the cart has earned a gift, or holds one — so ordinary
   * browsing never calls it. Answers are cached for STOCK_TTL_MS. With inventory tracked
   * and "Continue selling when out of stock" OFF, `available` flips to false at 0 units.
   *
   * A failed lookup keeps the last known answer: Shopify's own add.js / checkout still
   * refuse a sold-out variant, so failing open here cannot give away a 75th unit.
   */
  function refreshStock(cart, force) {
    if (!cfg.handle) return Promise.resolve(cart);
    var needed = force || computeRequired(cart) > 0 || giftLinesOf(cart).length > 0;
    if (!needed) return Promise.resolve(cart);
    if (!force && Date.now() - state.stockCheckedAt < STOCK_TTL_MS) return Promise.resolve(cart);

    return getJSON('/products/' + cfg.handle + '.js')
      .then(function (product) {
        state.stockCheckedAt = Date.now();
        var variants = (product && product.variants) || [];
        var available = null;
        for (var i = 0; i < variants.length; i++) {
          if (Number(variants[i].id) === cfg.variantId) available = variants[i].available !== false;
        }
        if (available === null) available = product ? product.available !== false : state.giftAvailable;
        if (available !== state.giftAvailable) log('gift stock changed — available:', available);
        state.giftAvailable = available;
        return cart;
      })
      .catch(function () {
        return cart;
      });
  }

  /** add.js answers 422 when the variant is out of stock. */
  function markSoldOut() {
    state.giftAvailable = false;
    state.stockCheckedAt = Date.now();
    warn('the gift is sold out — FORMEONLY can no longer add it');
  }

  /* ------------------------------------------------------------------ *
   * Discount code
   * ------------------------------------------------------------------ */

  /**
   * Apply the promotion code to the cart.
   *
   * The typed value is checked against the configured code first, so a customer typing
   * an unrelated code never has it silently applied to their cart. Shopify remains the
   * authority on whether the code is live — if the cart comes back without it applied,
   * the attribute is cleared again so the gate stays shut.
   */
  function applyCode(entered) {
    var value = String(entered === undefined ? cfg.code : entered || '').trim().toUpperCase();

    if (!value) return Promise.resolve({ ok: false, reason: 'invalid' });
    if (cfg.code && value !== cfg.code) return Promise.resolve({ ok: false, reason: 'invalid' });
    if (state.codeBusy) return Promise.resolve({ ok: false, reason: 'busy' });

    state.codeBusy = true;
    // A fresh attempt gets a fresh verdict — e.g. after the merchant activates the code.
    state.codeRejected = false;
    state.reArmed = false;

    var attributes = {};
    attributes[cfg.codeAttribute] = value;

    return postJSON('/cart/update.js', { discount: value, attributes: attributes })
      .then(function () {
        return fetchCart();
      })
      .then(function (cart) {
        // The code did not stick to the cart at all — Shopify does not know it.
        if (!isCodeApplied(cart)) {
          return clearCodeAttribute().then(function () {
            return { ok: false, reason: 'invalid' };
          });
        }

        state.codeApplied = true;

        // Check stock now, so a customer under $75 is told the minis are gone instead of
        // being asked to spend more toward one.
        return refreshStock(cart, true).then(function () {
          if (!state.giftAvailable) return { ok: true, reason: 'sold-out' };

          // Accepted. Below the threshold there is nothing to unlock yet; above it, the
          // gift gets added by the reconciliation pass and then verified against Shopify.
          var shortfall = cfg.thresholdCents - eligibleSubtotal(cart);
          if (shortfall > 0) return { ok: true, reason: 'pending', shortfall: shortfall };
          return { ok: true, reason: 'applied' };
        });
      })
      .catch(function (error) {
        warn('could not apply code:', error && error.message ? error.message : error);
        return { ok: false, reason: 'failed' };
      })
      .then(function (result) {
        state.codeBusy = false;
        return reconcile('code:apply').then(function () {
          return result;
        });
      });
  }

  /** Remove the promotion code. Reconciliation then strips the gifts back out. */
  function removeCode() {
    if (state.codeBusy) return Promise.resolve({ ok: false, reason: 'busy' });
    state.codeBusy = true;

    return postJSON('/cart/update.js', { discount: '', attributes: buildBlankAttribute() })
      .catch(function (error) {
        warn('could not remove code:', error && error.message ? error.message : error);
        return null;
      })
      .then(function () {
        state.codeBusy = false;
        state.codeApplied = false;
        return reconcile('code:remove').then(function () {
          return { ok: true, reason: 'removed' };
        });
      });
  }

  function clearCodeAttribute() {
    return postJSON('/cart/update.js', { discount: '', attributes: buildBlankAttribute() }).catch(function () {
      return null;
    });
  }

  function buildBlankAttribute() {
    var attributes = {};
    attributes[cfg.codeAttribute] = '';
    return attributes;
  }

  /** Has the customer stored our code, even though Shopify is not reporting it? */
  function codeStoredOnCart(cart) {
    var attributes = (cart && cart.attributes) || {};
    return String(attributes[cfg.codeAttribute] || '').trim().toUpperCase() === cfg.code;
  }

  /**
   * A code applied below the minimum purchase amount may not stick on Shopify's side.
   * Once the cart does qualify, re-send it once so the gift can unlock. One attempt per
   * page — never a retry loop.
   */
  function reArmCodeIfNeeded(cart) {
    if (!cfg.codeRequired || state.reArmed || state.codeRejected) return cart;
    if (!codeStoredOnCart(cart)) return cart;
    // Only meaningful when Shopify reports codes at all and ours is absent from the list —
    // i.e. it was dropped. If it is listed (applicable or not) there is nothing to re-send.
    if (!cart || !cart.discount_codes) return cart;
    if (codeApplicability(cart) !== null) return cart;
    if (eligibleSubtotal(cart) < cfg.thresholdCents) return cart;

    state.reArmed = true;
    log('cart now qualifies — re-applying the stored code');

    return postJSON('/cart/update.js', { discount: cfg.code })
      .then(fetchCart)
      .catch(function () {
        return cart;
      });
  }

  function formatMoney(cents) {
    return cfg.currencySymbol + (Math.max(0, cents) / 100).toFixed(2);
  }

  function giftLinesOf(cart) {
    var items = (cart && cart.items) || [];
    var lines = [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftLine(items[i])) lines.push(items[i]);
    }
    return lines;
  }

  /**
   * The safety net that replaces gating on applicable:true.
   *
   * Conditions for declaring the code dead: Shopify explicitly reports it as NOT
   * applicable, the cart is over the threshold, and the gift is already sitting in the
   * cart — i.e. everything Shopify needs is in place and it still refuses. That means an
   * expired, inactive, wrongly-scoped or usage-capped discount, and the customer would be
   * charged for the gift. Pull it back out, once.
   *
   * If the Cart API does not report discount_codes at all (applicability === null), this
   * never fires and the cart attribute remains the gate — the documented limitation.
   */
  function verifyDiscountOrRollback(cart) {
    if (!cfg.codeRequired || state.codeRejected) return cart;
    if (codeApplicability(cart) !== false) return cart;
    if (eligibleSubtotal(cart) < cfg.thresholdCents) return cart; // simply not qualified yet

    var giftLines = giftLinesOf(cart);
    if (!giftLines.length) return cart; // nothing at risk

    state.codeRejected = true;
    warn('Shopify reports ' + cfg.code + ' as not applicable to this cart — removing the gift');

    var updates = {};
    for (var i = 0; i < giftLines.length; i++) updates[giftLines[i].key] = 0;

    return postJSON('/cart/update.js', {
      updates: updates,
      discount: '',
      attributes: buildBlankAttribute()
    })
      .then(fetchCart)
      .then(function (rolledBack) {
        state.codeApplied = false;
        renderCodeForms(cfg.labels.invalid, true);
        return rolledBack;
      })
      .catch(function () {
        return cart;
      });
  }

  /* ------------------------------------------------------------------ *
   * Code entry UI
   * ------------------------------------------------------------------ */
  var FORM_MARKUP =
    '<div class="fmo-code" data-fmo-code-form>' +
    '<div class="fmo-code__applied" data-fmo-code-applied>' +
    '<span data-fmo-code-applied-text></span>' +
    '<button type="button" class="fmo-code__remove" data-fmo-code-remove></button>' +
    '</div>' +
    '<div class="fmo-code__entry" data-fmo-code-entry>' +
    '<span class="fmo-code__label" data-fmo-code-label></span>' +
    '<div class="fmo-code__row">' +
    '<input type="text" class="fmo-code__input" data-fmo-code-input autocomplete="off" autocapitalize="characters" spellcheck="false">' +
    '<button type="button" class="btn fmo-code__apply" data-fmo-code-apply></button>' +
    '</div>' +
    '</div>' +
    '<div class="fmo-code__message" data-fmo-code-message role="status" aria-live="polite"></div>' +
    '</div>';

  var FORM_STYLES =
    '.fmo-code{margin:0 0 15px;}' +
    '.fmo-code__label{display:block;margin-bottom:6px;font-size:13px;}' +
    '.fmo-code__row{display:flex;gap:8px;}' +
    '.fmo-code__input{flex:1;min-width:0;margin:0;text-transform:uppercase;}' +
    '.fmo-code__apply{flex:0 0 auto;}' +
    '.fmo-code__applied{display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:13px;}' +
    '.fmo-code__remove{background:none;border:0;padding:0;text-decoration:underline;cursor:pointer;font-size:12px;}' +
    '.fmo-code__message{margin-top:6px;font-size:12px;}' +
    '.fmo-code__message--error{color:#d02e2e;}';

  function injectStyles() {
    if (document.getElementById('FMOGiftCodeStyles')) return;
    var style = document.createElement('style');
    style.id = 'FMOGiftCodeStyles';
    style.textContent = FORM_STYLES;
    (document.head || document.body).appendChild(style);
  }

  /**
   * Where can the code field go on this page?
   *
   * Cart markup differs by cart type (drawer / modal / page) and by which cart surface is
   * on screen, so this walks a priority list instead of depending on one anchor:
   *
   *   1. [data-fmo-code-mount]  — an explicit placeholder from snippets/formeonly-gift-code.liquid
   *   2. [data-discounts]       — present in both cart-drawer.liquid and main-cart.liquid
   *   3. .cart__checkout-wrapper — the checkout button block
   *   4. [data-subtotal]        — the subtotal row, last resort
   *
   * The first selector that matches anything wins, so the field lands in one predictable
   * place rather than several. Exported for tests.
   */
  function resolveMountPoints(doc) {
    doc = doc || document;
    var points = [];
    var i;

    var explicit = doc.querySelectorAll('[data-fmo-code-mount]');
    for (i = 0; i < explicit.length; i++) points.push({ mode: 'append', element: explicit[i] });
    if (points.length) return points;

    var selectors = ['[data-discounts]', '.cart__checkout-wrapper', '[data-subtotal]'];
    for (var s = 0; s < selectors.length; s++) {
      var nodes = doc.querySelectorAll(selectors[s]);
      if (!nodes.length) continue;
      for (i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        // Never split the inside of a subtotal row — go up to the row itself.
        if (selectors[s] === '[data-subtotal]') node = closest(node, '.cart__item-row') || node;
        points.push({ mode: 'before', element: node });
      }
      return points;
    }

    return points;
  }

  /** Put a code form in every cart surface that does not already have one. */
  function mountCodeForms() {
    if (!cfg.codeRequired) return;
    injectStyles();

    if (cfg.autoMountCodeForm) {
      var points = resolveMountPoints(document);

      if (!points.length) {
        // Normal on pages with no cart markup (and on an empty cart page). Only worth
        // shouting about if we are actually on the cart.
        if (isCartPage() && !state.mountWarned) {
          state.mountWarned = true;
          warn(
            'no place to mount the code field on this cart. Add {% render "formeonly-gift-code" %} ' +
              'where you want it, or check that the cart is not empty.'
          );
        }
      }

      for (var i = 0; i < points.length; i++) {
        var point = points[i];
        var parent = point.mode === 'append' ? point.element : point.element.parentNode;
        if (!parent || parent.querySelector('[data-fmo-code-form]')) continue;

        var holder = document.createElement('div');
        holder.innerHTML = FORM_MARKUP;
        var form = holder.firstChild;

        if (point.mode === 'append') parent.appendChild(form);
        else parent.insertBefore(form, point.element);
      }
    }

    renderCodeForms();
  }

  /**
   * Cart markup can appear after us — Impulse renders drawer contents client-side, and
   * apps re-render cart sections. Retry a few times, then rely on cart events.
   */
  function scheduleMountRetries() {
    if (!cfg.codeRequired || !cfg.autoMountCodeForm) return;
    var delays = [300, 1000, 2500];
    for (var i = 0; i < delays.length; i++) {
      setTimeout(function () {
        if (!document.querySelector('[data-fmo-code-form]')) mountCodeForms();
      }, delays[i]);
    }
  }

  function renderCodeForms(message, isError) {
    var forms = document.querySelectorAll('[data-fmo-code-form]');
    if (!forms.length) return;

    var applied = state.codeApplied;

    var appliedText = appliedMessage(state.lastCart);

    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var appliedBlock = form.querySelector('[data-fmo-code-applied]');
      var entryBlock = form.querySelector('[data-fmo-code-entry]');
      var messageBlock = form.querySelector('[data-fmo-code-message]');

      setText(form.querySelector('[data-fmo-code-label]'), cfg.labels.prompt);
      setText(form.querySelector('[data-fmo-code-apply]'), cfg.labels.apply);
      setText(form.querySelector('[data-fmo-code-remove]'), cfg.labels.remove);
      setText(form.querySelector('[data-fmo-code-applied-text]'), appliedText);

      var input = form.querySelector('[data-fmo-code-input]');
      if (input) input.setAttribute('placeholder', cfg.labels.placeholder);

      if (appliedBlock) appliedBlock.style.display = applied ? '' : 'none';
      if (entryBlock) entryBlock.style.display = applied ? 'none' : '';

      if (messageBlock) {
        messageBlock.textContent = message || '';
        messageBlock.className = 'fmo-code__message' + (isError ? ' fmo-code__message--error' : '');
      }
    }
  }

  function setText(element, text) {
    if (element) element.textContent = text;
  }

  /**
   * What the applied-code line should say right now.
   *
   * A code stays on the cart after the qualifying products are removed — which is
   * correct, the customer may add more — but telling someone their gift is "unlocked"
   * when the cart no longer qualifies is nonsense, so the message follows the cart.
   */
  function appliedMessage(cart) {
    // Sold out outranks "spend $X more" — never ask someone to spend toward a gift that
    // is gone.
    if (!state.giftAvailable) return cfg.code + ' ' + cfg.labels.soldOut;

    var earned = cart ? computeRequired(cart) : 0;
    if (earned > 0) {
      return cfg.code + ' ' + cfg.labels.applied.replace('%count%', earned);
    }
    var shortfall = cfg.thresholdCents - (cart ? eligibleSubtotal(cart) : 0);
    return cfg.code + ' ' + cfg.labels.appliedPending.replace('%amount%', formatMoney(shortfall));
  }

  function handleApplyFrom(element) {
    var form = closest(element, '[data-fmo-code-form]');
    var input = form ? form.querySelector('[data-fmo-code-input]') : null;
    var value = input ? input.value : cfg.code;

    renderCodeForms('', false);

    applyCode(value).then(function (result) {
      if (result.ok) {
        if (input) input.value = '';
        // The applied line already says either "unlocked" or "add $X more", so no extra
        // message is needed here.
        renderCodeForms('', false);
      } else if (result.reason !== 'busy') {
        renderCodeForms(result.reason === 'failed' ? cfg.labels.failed : cfg.labels.invalid, true);
      }
    });
  }

  function closest(element, selector) {
    while (element && element.nodeType === 1) {
      if (element.matches ? element.matches(selector) : false) return element;
      element = element.parentNode;
    }
    return null;
  }

  function installCodeUiListeners() {
    if (!cfg.codeRequired) return;

    // Delegated, so the form keeps working after Impulse re-renders the drawer.
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (closest(target, '[data-fmo-code-apply]')) {
        event.preventDefault();
        handleApplyFrom(target);
      } else if (closest(target, '[data-fmo-code-remove]')) {
        event.preventDefault();
        removeCode().then(function () {
          renderCodeForms('', false);
        });
      }
    });

    // Enter inside the cart form would submit it (and head to checkout) — intercept.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.keyCode !== 13) return;
      if (!closest(event.target, '[data-fmo-code-input]')) return;
      event.preventDefault();
      handleApplyFrom(event.target);
    });
  }

  /* ------------------------------------------------------------------ *
   * Keeping the gift out of the customer's hands
   *
   * The real guard is server-side: assign templates/product.formeonly-gift.liquid to
   * the gift product so its page has no purchase form at all. These are backstops for
   * quick-add buttons, quick views, and the window before that template is assigned.
   *
   * Note the gift product cannot simply be unpublished — the theme needs it purchasable
   * to add it with /cart/add.js — and Shopify URL redirects only work on URLs that 404.
   * ------------------------------------------------------------------ */

  /** Is this an add-to-cart form for the gift variant? */
  function isGiftForm(form) {
    if (!form || !cfg.variantId || !form.querySelector) return false;
    var action = (form.getAttribute && form.getAttribute('action')) || '';
    if (action.indexOf('/cart/add') === -1) return false;

    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return false;
    var value = idInput.value !== undefined ? idInput.value : idInput.getAttribute('value');
    return Number(value) === cfg.variantId;
  }

  /** Disable any storefront form that would add the gift on its own. */
  function guardGiftForms() {
    if (!cfg.blockDirectAdd) return;

    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      if (!isGiftForm(form) || form.getAttribute('data-fmo-gift-blocked')) continue;

      form.setAttribute('data-fmo-gift-blocked', 'true');

      var buttons = form.querySelectorAll('button, [type="submit"]');
      for (var b = 0; b < buttons.length; b++) {
        buttons[b].disabled = true;
        buttons[b].setAttribute('aria-disabled', 'true');
      }
      log('blocked a direct add-to-cart form for the gift product');
    }
  }

  /** Hide gift product cards from collection and search listings. */
  function hideGiftFromListings() {
    if (!cfg.hideFromListings || !cfg.handle) return;

    var links = document.querySelectorAll('a[href*="/products/' + cfg.handle + '"]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      // Never touch the cart's own line items — the customer must still see their gift.
      if (closest(link, '[data-location]') || closest(link, '.cart__item') || closest(link, '#CartDrawer')) {
        continue;
      }
      var card = closest(link, '.grid__item');
      if (card && card.style) card.style.display = 'none';
    }
  }

  function applyStorefrontGuards() {
    guardGiftForms();
    hideGiftFromListings();
  }

  function installStorefrontGuards() {
    if (!cfg.blockDirectAdd && !cfg.hideFromListings) return;

    applyStorefrontGuards();

    // Submit-capture, so a re-enabled button or a themed quick-add still cannot post.
    document.addEventListener(
      'submit',
      function (event) {
        if (cfg.blockDirectAdd && isGiftForm(event.target)) {
          event.preventDefault();
          event.stopPropagation();
          warn('direct add-to-cart for the gift product was blocked');
        }
      },
      true
    );

    // Grids load lazily (pagination, filters, quick view), so re-apply as the DOM changes.
    if (window.MutationObserver) {
      var pending = null;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () {
          pending = null;
          applyStorefrontGuards();
        }, 300);
      });
      observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
    }
  }

  /** Marketing links: /discount/FORMEONLY?redirect=/cart%3Ffmo_code%3DFORMEONLY */
  function codeFromUrl() {
    var search = (window.location && window.location.search) || '';
    var match = search.match(/[?&](?:fmo_code|discount)=([^&]+)/i);
    return match ? decodeURIComponent(match[1]).trim().toUpperCase() : null;
  }

  /* ------------------------------------------------------------------ *
   * UI refresh — Impulse rebuilds drawer + cart page on `cart:build`
   * ------------------------------------------------------------------ */
  function dispatch(name, detail) {
    var evt;
    try {
      evt = new CustomEvent(name, { detail: detail, bubbles: true });
    } catch (e) {
      evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(name, true, true, detail);
    }
    document.dispatchEvent(evt);
  }

  function isCartPage() {
    return window.location && /\/cart(\/|\?|$)/.test(window.location.pathname || '');
  }

  function refreshUI(cart) {
    dispatch('cart:build', { cart: cart });
    if (cfg.reloadCartPage && isCartPage() && window.location.reload) {
      window.location.reload();
    }
  }

  /* ------------------------------------------------------------------ *
   * Reconciliation
   * ------------------------------------------------------------------ */
  function burstAllowed() {
    var now = Date.now();
    if (now < state.pausedUntil) return false;

    var times = state.passTimes;
    times.push(now);
    while (times.length && now - times[0] > BURST_WINDOW_MS) times.shift();

    if (times.length > BURST_LIMIT) {
      state.pausedUntil = now + BURST_COOLDOWN_MS;
      state.passTimes = [];
      warn('too many cart updates in a row — pausing gift reconciliation for a moment');
      return false;
    }
    return true;
  }

  function reconcile(trigger) {
    if (!cfg.enabled || state.disabled) return Promise.resolve(null);

    if (state.busy) {
      state.queued = true;
      log('pass already running, queued', trigger);
      return Promise.resolve(null);
    }
    if (!burstAllowed()) return Promise.resolve(null);

    state.busy = true;

    return fetchCart()
      .then(reArmCodeIfNeeded)
      .then(verifyDiscountOrRollback)
      .then(refreshStock)
      .then(function (cart) {
        state.lastCart = cart;
        state.codeApplied = isCodeApplied(cart);
        mountCodeForms(); // idempotent, and self-heals if the cart markup arrived late

        var signature = cartSignature(cart);
        if (signature === state.lastSettledSignature) {
          log('cart unchanged since last settled pass — skipping', trigger);
          return null;
        }

        var plan = planReconciliation(cart);
        log('trigger:', trigger, 'plan:', plan);

        if (plan.action === 'none') {
          state.lastSettledSignature = signature;
          return null;
        }

        var write =
          plan.action === 'add'
            ? postJSON('/cart/add.js', { id: cfg.variantId, quantity: plan.addQuantity }).catch(function (error) {
                // 422 = sold out (or not enough stock). The last unit went between the
                // stock check and the add — not a failure, the promotion is just over.
                if (error && error.status === 422) {
                  markSoldOut();
                  return null;
                }
                throw error;
              })
            : postJSON('/cart/update.js', { updates: plan.updates });

        return write.then(function () {
          state.writeFailures = 0;
          return fetchCart()
            .then(verifyDiscountOrRollback)
            .then(function (updatedCart) {
              // The write we just made is the settled state; record it so the
              // cart:updated echo it causes does not start another pass.
              state.lastSettledSignature = cartSignature(updatedCart);
              return updatedCart;
            });
        });
      })
      .catch(function (error) {
        state.writeFailures++;
        warn('reconciliation failed:', error && error.message ? error.message : error);
        if (state.writeFailures >= MAX_WRITE_FAILURES) {
          state.disabled = true;
          warn('disabling gift reconciliation for this page after repeated failures');
        }
        return null;
      })
      .then(function (updatedCart) {
        state.busy = false;

        if (updatedCart) refreshUI(updatedCart);

        if (state.queued) {
          state.queued = false;
          return reconcile('queued');
        }
        return updatedCart;
      });
  }

  function schedule(trigger) {
    if (!cfg.enabled || state.disabled) return;
    if (state.debounceTimer) clearTimeout(state.debounceTimer);
    state.debounceTimer = setTimeout(function () {
      state.debounceTimer = null;
      reconcile(trigger);
    }, DEBOUNCE_MS);
  }

  /* ------------------------------------------------------------------ *
   * Triggers
   * ------------------------------------------------------------------ */
  function installInterceptors() {
    // Catches the Heirloom builder's direct fetch('/cart/add.js'), Impulse's own
    // quantity writes, and any app that mutates the cart. Same pattern already used
    // by promo-sticky-banner.liquid in this store.
    if (window.fetch) {
      var patchedFetch = window.fetch;
      window.fetch = function () {
        var input = arguments[0];
        var url = input ? input.url || input : '';
        var result = patchedFetch.apply(this, arguments);
        if (typeof url === 'string' && CART_MUTATION_RE.test(url) && result && result.then) {
          result
            .then(function () {
              schedule('fetch:' + url);
            })
            .catch(function () {});
        }
        return result;
      };
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      var nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          if (typeof url === 'string' && CART_MUTATION_RE.test(url)) {
            this.addEventListener('load', function () {
              schedule('xhr:' + url);
            });
          }
        } catch (e) {
          /* never block the request */
        }
        return nativeOpen.apply(this, arguments);
      };
    }
  }

  function installListeners() {
    // Impulse events. `cart:build` is dispatched BY us, never listened to, so the
    // refresh we trigger cannot feed straight back into a write.
    document.addEventListener('cart:updated', function () {
      mountCodeForms(); // the drawer was just re-rendered — put the form back
      schedule('cart:updated');
    });
    document.addEventListener('ajaxProduct:added', function () {
      schedule('ajaxProduct:added');
    });
    document.addEventListener('cart:open', function () {
      mountCodeForms();
      schedule('cart:open');
    });
    window.addEventListener('pageshow', function (evt) {
      if (evt && evt.persisted) schedule('pageshow');
    });
  }

  function init() {
    if (!cfg.enabled) return;
    if (!nativeFetch) {
      warn('fetch unavailable — gift reconciliation disabled');
      return;
    }
    if (!cfg.variantId) {
      warn('no gift variant configured — set the gift product in Theme settings');
      return;
    }

    installInterceptors();
    installListeners();
    installCodeUiListeners();
    installStorefrontGuards();
    mountCodeForms();
    scheduleMountRetries();

    // A /discount/FORMEONLY?redirect=/cart?fmo_code=FORMEONLY link applies the code for the
    // customer, so campaign traffic never has to type it.
    var urlCode = cfg.codeRequired ? codeFromUrl() : null;
    if (urlCode && urlCode === cfg.code) {
      applyCode(urlCode).then(function () {
        renderCodeForms('', false);
      });
      return;
    }

    reconcile('init');
  }

  /* Exposed for tests, debugging and future extensions. */
  window.FormeonlyGift = {
    version: VERSION,
    config: cfg,
    isGiftLine: isGiftLine,
    eligibleSubtotal: eligibleSubtotal,
    isCodeApplied: isCodeApplied,
    codeApplicability: codeApplicability,
    computeRequired: computeRequired,
    planReconciliation: planReconciliation,
    cartSignature: cartSignature,
    reconcile: reconcile,
    applyCode: applyCode,
    removeCode: removeCode,
    mountCodeForms: mountCodeForms,
    resolveMountPoints: resolveMountPoints,
    isGiftForm: isGiftForm,
    applyStorefrontGuards: applyStorefrontGuards,
    isEligibleLine: isEligibleLine,
    refreshStock: refreshStock,
    appliedMessage: appliedMessage,
    state: state,
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
